import { parseItemsToIds } from 'utils/parseItemsToIds';
import { parseToItems } from 'utils/parseToItems';

import { useForm } from '@american-autonomy/ag-ui';

import useApplication from 'hooks/useApplication';
import useBoundary from 'hooks/useBoundary';
import useField from 'hooks/useField';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';

import type { ApplicationUpdateParams, Props, UpdateApplicationFormSchema } from './types';

const useData = ({ formValues, isClosing, onClose, setIsClosing }: Props) => {
    const { updateApplication } = useApplication();
    const { fetchBoundary } = useBoundary();
    const { assignFieldToBoundary, fetchFieldHierarchyFormItems } = useField();

    const getFieldData = async (): Promise<UpdateApplicationFormSchema> => {
        let parsedFormData: Partial<UpdateApplicationFormSchema> =
            formValues as unknown as UpdateApplicationFormSchema;

        if (formValues?.site?.id) {
            const fieldData = await fetchFieldHierarchyFormItems({ id: formValues.site.id });

            parsedFormData = {
                ...parsedFormData,
                ...fieldData
            };
        }

        if (formValues?.drones) {
            parsedFormData = {
                ...parsedFormData,
                drones: parseToItems(formValues.drones)
            };
        }

        if (formValues?.boundaryId) {
            const boundary = await fetchBoundary({ id: formValues.boundaryId });

            parsedFormData = {
                ...parsedFormData,
                boundaryId: {
                    label: boundary?.name ?? '',
                    value: boundary?.id ?? ''
                }
            };
        }

        if (formValues?.batteries) {
            parsedFormData = {
                ...parsedFormData,
                batteries: parseToItems(formValues.batteries)
            };
        }

        if (formValues?.scheduledPilots) {
            parsedFormData = {
                ...parsedFormData,
                pilotIds: formValues.scheduledPilots.map(pilot => ({
                    label: `${pilot.firstName ?? ''} ${pilot.lastName ?? ''}`.trim(),
                    value: pilot.id
                }))
            };
        }

        return parsedFormData as unknown as UpdateApplicationFormSchema;
    };

    const form = useForm<UpdateApplicationFormSchema>({
        defaultValues: getFieldData,
        mode: 'onChange'
    });

    const {
        control,
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose: () => setIsClosing?.(false),
        onClose,
        requestingClose: isClosing
    });

    const onSubmit = async (data: UpdateApplicationFormSchema) => {
        const { batteries, boundaryId, drones, field, name, pilotIds } = data;

        if (!formValues) {
            return;
        }

        const params: ApplicationUpdateParams = {
            application: {
                applicationSettings: formValues?.applicationSettings,
                batteryIds: parseItemsToIds(batteries),
                boundaryId: boundaryId?.value ?? '',
                droneIds: parseItemsToIds(drones),
                name: name ?? '',
                scheduledPilotIds:
                    pilotIds && pilotIds[0] !== null ? parseItemsToIds(pilotIds) : [],
                siteId: field?.value ?? '',
                status: data.status
            },
            applicationId: formValues.id
        };

        if (field?.value && boundaryId?.value && boundaryId.siteId !== field?.value) {
            await assignFieldToBoundary({
                boundaryId: boundaryId.value,
                siteId: field?.value
            });
        }

        const application = await updateApplication(params);

        if (application) {
            onClose?.();
        }
    };

    return {
        confirmationPanel,
        control,
        errors,
        form,
        handleSubmit,
        onSubmit,
        showConfirmation
    };
};

export default useData;
